import React from 'react';

interface PaginationProps {
  pageNo: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  hasNext: boolean;
  onPageChange: (page: number) => void;
}

type PageItem = number | 'ellipsis';

/** Always shows first/last page, the current page ± 1 sibling, and collapses
 * the rest behind an ellipsis once there are more than 7 pages to list. */
function getPageItems(current: number, totalPages: number): PageItem[] {
  const last = totalPages - 1;
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  const siblings = 1;
  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, last - 1);

  const items: PageItem[] = [0];
  if (left > 1) items.push('ellipsis');
  for (let page = left; page <= right; page += 1) items.push(page);
  if (right < last - 1) items.push('ellipsis');
  items.push(last);
  return items;
}

const Pagination: React.FC<PaginationProps> = ({
  pageNo,
  totalPages,
  totalElements,
  pageSize,
  hasNext,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const rangeStart = pageNo * pageSize + 1;
  const rangeEnd = Math.min(totalElements, (pageNo + 1) * pageSize);
  const items = getPageItems(pageNo, totalPages);

  return (
    <nav className="players-pagination" aria-label="Players pagination">
      <div className="players-pagination__summary">
        Showing {rangeStart}–{rangeEnd} of {totalElements} players
      </div>
      <div className="players-pagination__controls">
        <button
          type="button"
          className="players-pagination__btn players-pagination__btn--nav"
          disabled={pageNo === 0}
          onClick={() => onPageChange(pageNo - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>

        {items.map((item, index) =>
          item === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="players-pagination__ellipsis">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`players-pagination__btn ${item === pageNo ? 'active' : ''}`}
              aria-current={item === pageNo ? 'page' : undefined}
              onClick={() => onPageChange(item)}
            >
              {item + 1}
            </button>
          )
        )}

        <button
          type="button"
          className="players-pagination__btn players-pagination__btn--nav"
          disabled={!hasNext}
          onClick={() => onPageChange(pageNo + 1)}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
